import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MapPin, CalendarDays, Heart, Users, Globe, ExternalLink,
  Instagram, Facebook, Youtube, Share2, Mail, UserPlus, UserCheck,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { communityAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../lib/utils';
import { SafeImg } from '../components/SafeImg';

const SOCIAL_ICON_MAP = {
  instagram: Instagram,
  facebook: Facebook,
  youtube: Youtube,
  // Tiktok & Pinterest lack dedicated Lucide icons; we render their label fallback.
};

const formatJoined = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } catch {
    return '—';
  }
};

export function VendorProfile() {
  const { slug } = useParams();
  const { user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [events, setEvents] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null); // url or null

  useEffect(() => {
    setNotFound(false);
    setLoading(true);
    (async () => {
      try {
        const { data } = await communityAPI.getVendor(slug);
        setProfile(data);
        setFollowersCount(data.followers_count || 0);
        const evRes = await communityAPI.getVendorEvents(slug);
        setEvents(evRes.data || []);
        if (me && me.id !== data.id) {
          const s = await communityAPI.followStatus(data.id);
          setIsFollowing(s.data.is_following);
          setFollowersCount(s.data.followers_count);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, me]);

  const onToggleFollow = async () => {
    if (!me) {
      toast.info('Sign in to follow vendors');
      return;
    }
    try {
      if (isFollowing) {
        const { data } = await communityAPI.unfollow(profile.id);
        setIsFollowing(false);
        setFollowersCount(data.followers_count);
        toast.success(`Unfollowed ${profile.name}`);
      } else {
        const { data } = await communityAPI.follow(profile.id);
        setIsFollowing(true);
        setFollowersCount(data.followers_count);
        toast.success(`Following ${profile.name}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Something went wrong');
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/v/${profile.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: profile.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Profile link copied to clipboard');
      }
    } catch {
      /* user cancelled */
    }
  };

  const inviteFriend = () => {
    const url = `${window.location.origin}/v/${profile.slug}`;
    const subject = encodeURIComponent(`Check out ${profile.name} on AMSCompass`);
    const body = encodeURIComponent(
      `Thought you might enjoy browsing ${profile.name}'s vendor profile and upcoming shows on AMSCompass:\n\n${url}`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return <div className="text-center py-24 text-[#71717A]">Loading profile…</div>;
  }
  if (notFound || !profile) {
    return (
      <div className="text-center py-24">
        <Users className="w-10 h-10 text-[#71717A] mx-auto mb-3" />
        <h2 className="text-xl font-semibold text-[#1E3A5F]">Vendor not found</h2>
        <p className="text-sm text-[#4A4A4A] mt-1">
          This profile doesn&rsquo;t exist or is set to private.
        </p>
        <Link to="/community" className="inline-block mt-4">
          <Button className="btn-primary">Browse Community</Button>
        </Link>
      </div>
    );
  }

  const socials = profile.social_links || {};
  const initial = (profile.name || 'V')[0].toUpperCase();

  return (
    <div data-testid="vendor-profile-page">
      {/* Cover banner */}
      <div className="relative h-56 md:h-80 bg-gradient-to-br from-[#1E3A5F] via-[#1E3A5F] to-[#FE5F55] overflow-hidden">
        <SafeImg
          src={profile.cover_photo_url}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      </div>

      {/* Header card */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 flex flex-col md:flex-row gap-5 items-start">
          {/* Profile photo */}
          <div className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-white border-4 border-white shadow overflow-hidden flex items-center justify-center text-4xl font-extrabold text-[#1E3A5F] -mt-14 md:-mt-20">
            <SafeImg
              src={profile.profile_photo_url || profile.logo_url}
              alt={profile.name}
              className="w-full h-full object-cover"
              fallback={<span>{initial}</span>}
            />
          </div>
          {/* Name, tagline */}
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl md:text-4xl font-extrabold text-[#1E3A5F] font-['Outfit'] tracking-tight">
              {profile.name}
            </h1>
            {profile.tagline && (
              <p className="text-base text-[#4A4A4A] mt-1">{profile.tagline}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-sm text-[#71717A]">
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-4 h-4 text-[#FE5F55]" />
                {[profile.home_city, profile.home_state].filter(Boolean).join(', ') || 'Location not set'}
              </span>
              {profile.vendor_category && (
                <span className="px-2 py-0.5 rounded-full bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs font-semibold">
                  {profile.vendor_category}
                </span>
              )}
            </div>
          </div>
          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {me && me.id === profile.id ? (
              <Link to="/profile/edit">
                <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F]" data-testid="edit-vendor-profile-btn">
                  Edit Profile
                </Button>
              </Link>
            ) : (
              <Button
                onClick={onToggleFollow}
                className={isFollowing ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'btn-primary'}
                data-testid="follow-btn"
              >
                {isFollowing ? (
                  <><UserCheck className="w-4 h-4 mr-1.5" />Following</>
                ) : (
                  <><UserPlus className="w-4 h-4 mr-1.5" />Follow</>
                )}
              </Button>
            )}
            <Button
              onClick={share}
              variant="outline"
              className="border-gray-300 text-[#4A4A4A]"
              data-testid="share-btn"
            >
              <Share2 className="w-4 h-4 mr-1.5" />
              Share
            </Button>
            <Button
              onClick={inviteFriend}
              variant="outline"
              className="border-gray-300 text-[#4A4A4A]"
              data-testid="invite-friend-btn"
            >
              <Mail className="w-4 h-4 mr-1.5" />
              Invite a Friend
            </Button>
          </div>
        </div>

        {/* Public stats */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="vendor-stats">
          <StatTile label="Joined" value={formatJoined(profile.joined_at)} />
          <StatTile label="Upcoming shows" value={profile.saved_events_count} testid="stat-saved" />
          <StatTile label="Past shows attended" value={profile.past_events_count} testid="stat-past" />
          <StatTile label="Followers" value={followersCount} testid="stat-followers" />
        </div>

        {/* Bio + links + gallery + events */}
        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* Left: bio + links */}
          <div className="lg:col-span-1 space-y-4">
            {profile.bio && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#71717A] mb-2">About</h2>
                <p className="text-sm text-[#4A4A4A] whitespace-pre-line">{profile.bio}</p>
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#71717A] mb-3">Links</h2>
              <div className="flex flex-wrap gap-2">
                {profile.website_url && (
                  <ExternalLinkChip
                    href={profile.website_url}
                    label="Website"
                    Icon={Globe}
                    testid="link-website"
                  />
                )}
                {profile.etsy_url && (
                  <ExternalLinkChip
                    href={profile.etsy_url}
                    label="Etsy Store"
                    Icon={ExternalLink}
                    testid="link-etsy"
                  />
                )}
                {Object.entries(socials).map(([platform, url]) => {
                  if (!url) return null;
                  const Icon = SOCIAL_ICON_MAP[platform] || ExternalLink;
                  return (
                    <ExternalLinkChip
                      key={platform}
                      href={url}
                      label={platform.charAt(0).toUpperCase() + platform.slice(1)}
                      Icon={Icon}
                      testid={`link-${platform}`}
                    />
                  );
                })}
                {!profile.website_url && !profile.etsy_url &&
                  !Object.values(socials).some(Boolean) && (
                    <span className="text-xs text-[#71717A]">No links added yet.</span>
                  )}
              </div>
            </div>
          </div>

          {/* Middle/right: gallery + events */}
          <div className="lg:col-span-2 space-y-6">
            {profile.gallery && profile.gallery.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h2 className="text-sm font-bold uppercase tracking-wider text-[#71717A] mb-3">
                  Gallery ({profile.gallery.length})
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {profile.gallery.map((url, i) => (
                    <button
                      key={i}
                      onClick={() => setLightbox(url)}
                      className="aspect-square rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-[#FE5F55] transition"
                      data-testid={`gallery-${i}`}
                    >
                      <SafeImg src={url} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#71717A] mb-3">
                Upcoming Shows ({events.length})
              </h2>
              {events.length === 0 ? (
                <p className="text-sm text-[#71717A]">No upcoming events announced.</p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {events.map((e) => (
                    <li key={e.id} className="py-3 flex items-start gap-3">
                      <Link to={`/events/${e.id}`} className="flex-1 min-w-0 hover:text-[#1E3A5F]">
                        <div className="font-bold text-[#1E3A5F] font-['Outfit'] line-clamp-1">{e.title}</div>
                        <div className="text-xs text-[#71717A] mt-0.5 inline-flex items-center gap-3">
                          <span className="inline-flex items-center gap-1">
                            <CalendarDays className="w-3 h-3" />{formatDate(e.date)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {[e.city, e.state].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      </Link>
                      <Heart className="w-4 h-4 text-[#FE5F55] fill-current flex-shrink-0" />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="h-12" />
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-zoom-out"
          data-testid="gallery-lightbox"
        >
          <SafeImg src={lightbox} alt="" className="max-h-full max-w-full rounded-lg shadow-2xl" />
        </div>
      )}
    </div>
  );
}

const StatTile = ({ label, value, testid }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 text-center" data-testid={testid}>
    <div className="text-2xl font-extrabold text-[#1E3A5F] font-['Outfit']">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-1">{label}</div>
  </div>
);

const ExternalLinkChip = ({ href, label, Icon, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1E3A5F]/5 hover:bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs font-semibold border border-[#1E3A5F]/10 transition"
    data-testid={testid}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
  </a>
);
